import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SetMemberPasswordDialog from '../components/SetMemberPasswordDialog';
import { ApiService } from '../utils/api';
import { formatDate } from '../utils/datetime';
import { useToast } from '../components/Toast';
import { 
  ArrowLeft, 
  Plus, 
  Mail, 
  User, 
  Shield, 
  Edit, 
  Trash2,
  Send,
  CheckCircle,
  KeyRound
} from 'lucide-react';
import { useEventRoute } from '../utils/eventRef';

const TeamManagement = () => {
  const { eventId } = useEventRoute();
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [newMember, setNewMember] = useState({
    email: '',
    role: 'viewer'
  });
  const [editingMember, setEditingMember] = useState(null);
  const [passwordMember, setPasswordMember] = useState(null);

  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();


  // Fetch team members
  const { data: membersData, isLoading } = useQuery({
    queryKey: ['teamMembers', eventId],
    queryFn: () => ApiService.getTeamMembers(eventId),
    enabled: !!eventId,
  });

  // Ensure members is always an array
  const members = Array.isArray(membersData) ? membersData : [];

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: (memberData) => ApiService.inviteTeamMember(eventId, memberData),
    onSuccess: () => {
      queryClient.invalidateQueries(['teamMembers', eventId]);
      setIsAddingMember(false);
      setNewMember({ email: '', role: 'viewer' });
      showSuccess('Team member invited successfully!');
    },
    onError: (error) => {
      showError('Failed to invite team member. Please try again.');
    }
  });

  // Update member role mutation
  const updateMemberMutation = useMutation({
    mutationFn: ({ memberId, role }) => 
      ApiService.updateTeamMember(eventId, memberId, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries(['teamMembers', eventId]);
      setEditingMember(null);
      showSuccess('Member role updated successfully!');
    },
    onError: (error) => {
      showError('Failed to update member role. Please try again.');
    }
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: (memberId) => ApiService.removeTeamMember(eventId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries(['teamMembers', eventId]);
      showSuccess('Member removed successfully!');
    },
    onError: (error) => {
      showError('Failed to remove member. Please try again.');
    }
  });

  const handleAddMember = () => {
    if (!newMember.email.trim()) {
      showError('Email is required');
      return;
    }
    addMemberMutation.mutate(newMember);
  };

  const handleUpdateRole = (memberId, newRole) => {
    updateMemberMutation.mutate({ memberId, role: newRole });
  };

  const handleRemoveMember = (memberId) => {
    if (window.confirm('Are you sure you want to remove this team member?')) {
      removeMemberMutation.mutate(memberId);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="h-4 w-4 text-red-500" />;
      case 'editor':
        return <Edit className="h-4 w-4 text-blue-500" />;
      case 'viewer':
        return <User className="h-4 w-4 text-gray-500" />;
      default:
        return <User className="h-4 w-4 text-gray-500" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'editor':
        return 'bg-blue-100 text-blue-800';
      case 'viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/admin/events">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
            <p className="text-gray-600">Manage team members and their permissions</p>
          </div>
        </div>
        <Button onClick={() => setIsAddingMember(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Member
        </Button>
      </div>


      {/* Add Member Form */}
      {isAddingMember && (
        <Card>
          <CardHeader>
            <CardTitle>Invite Team Member</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="team@example.com"
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Select 
                  value={newMember.role} 
                  onValueChange={(value) => setNewMember(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsAddingMember(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddMember}
                disabled={addMemberMutation.isPending}
              >
                {addMemberMutation.isPending ? 'Sending...' : 'Send Invite'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Members List */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members ({members.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
              <p className="text-gray-600 mb-4">Invite team members to collaborate on this event.</p>
              <Button onClick={() => setIsAddingMember(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Member
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {members?.map((member) => (
                <div key={member.memberId} className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-gray-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900">{member.name || member.email}</h3>
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(member.role)}`}>
                          {getRoleIcon(member.role)}
                          <span className="ml-1 capitalize">{member.role}</span>
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <Mail className="h-4 w-4 mr-1" />
                        {member.email}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Invited {formatDate(member.invitedAt)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {editingMember === member.memberId ? (
                      <div className="flex items-center space-x-2">
                        <Select
                          value={member.role}
                          onValueChange={(newRole) => handleUpdateRole(member.memberId, newRole)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="viewer">Viewer</SelectItem>
                            <SelectItem value="editor">Editor</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingMember(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingMember(member.memberId)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setPasswordMember(member)}>
                          <KeyRound className="h-4 w-4 mr-1" />
                          Password
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveMember(member.memberId)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Permissions */}
      <Card>
        <CardHeader>
          <CardTitle>Role Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center mb-3">
                <Shield className="h-5 w-5 text-red-500 mr-2" />
                <h3 className="font-medium text-gray-900">Admin</h3>
              </div>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Create and manage events</li>
                <li>• View all registrations</li>
                <li>• Export data</li>
                <li>• Scan QR codes</li>
                <li>• Manage team members</li>
                <li>• View analytics</li>
              </ul>
            </div>
            
            <div>
              <div className="flex items-center mb-3">
                <Edit className="h-5 w-5 text-blue-500 mr-2" />
                <h3 className="font-medium text-gray-900">Editor</h3>
              </div>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Edit events</li>
                <li>• View registrations</li>
                <li>• Export data</li>
                <li>• Scan QR codes</li>
                <li>• Limited team access</li>
              </ul>
            </div>
            
            <div>
              <div className="flex items-center mb-3">
                <User className="h-5 w-5 text-gray-500 mr-2" />
                <h3 className="font-medium text-gray-900">Viewer</h3>
              </div>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• View registrations</li>
                <li>• Scan QR codes</li>
                <li>• Read-only access</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
      <SetMemberPasswordDialog eventId={eventId} member={passwordMember} onClose={() => setPasswordMember(null)} />
    </div>
  );
};

export default TeamManagement;
